import React, { useContext, useMemo, useState, useEffect } from 'react';
import cx from 'classnames';
import styled from '@emotion/styled';
import { isEmpty, partial, get } from 'lodash';
import { useMedia } from 'react-use-media';
// eslint-disable-next-line no-restricted-imports
import { Checkbox, Image } from 'react-bootstrap';
import createPersistedState from '~/react-hooks/use-persisted-state';

import colours from '~/utils/colours';
import { createRestApi } from '~/App/Api/rest';
import Todo from '~/App/Components/Todo/Simple';
import { Step } from '~/App/Components/Typeform';
import StepContext from '~/App/Components/Typeform/StepContext';
import useEventListener from '~/react-hooks/use-event-listener';
import ActionButton from '~/App/Components/Typeform/ActionButton';
import deleteGreetsAction from '~/actions/greets/deleteGreetsAction';
import { TodoCreateMode } from '~/App/Modules/TodoList/Modals/TodoCreate';
import type { Greet, Profile, UserSimple } from '~/types/models';
import Workspace from '~/App/Modules/Workspaces/Types/Workspace';

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledTodo = styled(Todo)`
  border-radius: 8px;
  border: 1px solid ${colours.greyMedium};
`;

const RollUpText = styled.span`
  color: ${colours.greyMediumDark};
`;

const RollinImageContainer = styled.div`
  display: flex;
  margin-top: 30px;
  justify-content: center;
`;

export { StyledTodo as Todo };

const useRollinState = createPersistedState('todo-rollups-show-rollin-gif');

export type ConfirmProps = {
  note?: string;
  assignee: UserSimple;
  contactUpdates: Record<string, Record<string, Partial<Profile>>>;
  contactsAsProfiles: Profile[];
  mode: string;
  greets: Greet[];
  workspace: Workspace;
};

const Confirm = React.memo(
  ({
    mode,
    assignee,
    contactsAsProfiles,
    note,
    contactUpdates,
    greets,
    workspace,
  }: ConfirmProps) => {
    const [redirect, setRedirect] = useState<string | null>(null);
    const [recordMode, setRecordMode] = useState(false);
    const [restart, setRestart] = useState(false);
    const [showRollinGif, setShowRollinGif] = useRollinState(true);

    const greetsApi = useMemo(() => createRestApi<Greet>('/api/v2/greets'), []);
    const profilesApi = useMemo(() => createRestApi('/api/v2/profiles'), []);
    const profileIds = useMemo(
      () => contactsAsProfiles.map((profile) => profile.id),
      [contactsAsProfiles]
    );
    const profileUpdates = useMemo(
      () =>
        Object.keys(contactUpdates).map((id) => ({
          id,
          name: contactUpdates[id].name,
        })),
      [contactUpdates]
    );

    const hasValidationError = useMemo(() => {
      return (
        !assignee ||
        contactsAsProfiles.length === 0 ||
        Object.keys(contactUpdates).some((i) => contactUpdates[i].name === '')
      );
    }, [assignee, contactsAsProfiles, contactUpdates]);

    const [isLoading, setLoading] = useState(false);

    const { focus } = useContext(StepContext);

    async function handleCreateClick(recordMode: boolean) {
      setLoading(true);

      // update profiles if there are any changes
      if (!isEmpty(profileUpdates)) {
        await profilesApi.patch({
          profiles: profileUpdates,
        });
      }

      // create the greet
      // campaign_id can be null
      const res = await greetsApi.post({
        assignee_id: assignee.id,
        profiles: profileIds,
        campaign_id: get(workspace, 'uuid'),
        note,
      });

      if (mode == TodoCreateMode.ROLLUP) {
        setShowRollinGif(false);
        await deleteGreetsAction(greets.map((greet) => greet.id));
      }

      if (recordMode) {
        setRedirect(`/home/greet/${res.data.id}?openRecorder=true`);
      } else {
        setRedirect(
          restart ? location.toString() : `/home/greet/${res.data.id}`
        );
      }
    }

    useEffect(() => {
      if (redirect != null) {
        if (location.toString() == redirect) {
          location.reload();
        } else {
          location = redirect;
        }
      }
    }, [redirect]);

    useEventListener('keyup', (evt) => {
      if (!evt.shiftKey && recordMode) setRecordMode(false);
    });

    useEventListener('keydown', (evt) => {
      if (evt.shiftKey && !recordMode) setRecordMode(true);
    });

    function handleRestartClicked(evt: React.FormEvent<Checkbox>) {
      setRestart((evt.target as HTMLInputElement).checked);
      focus();
    }

    const isMobile = useMedia({ maxWidth: 768 });
    const buttonText = useMemo(
      () => (recordMode ? 'Create & Record' : 'Create'),
      [recordMode]
    );
    const helpText = useMemo(
      () => (isMobile ? 'Press ENTER' : 'Press ENTER (+ SHIFT to record)'),
      [isMobile]
    );

    return (
      <Step heading='Confirm & Create'>
        {contactsAsProfiles.length > 0 && (
          <StyledTodo profiles={contactsAsProfiles} reason={note} />
        )}
        {mode == TodoCreateMode.ROLLUP && (
          <RollUpText>{greets.length} To-Dos will be merged</RollUpText>
        )}
        <ActionContainer>
          <ActionButton
            tabIndex={-1}
            helpText={helpText}
            loading={isLoading}
            onClick={partial(handleCreateClick, recordMode)}
            desktopText={buttonText}
            mobileText={buttonText}
            disabled={hasValidationError}
          />
          <Checkbox
            className={cx({
              hidden: recordMode || mode == TodoCreateMode.ROLLUP,
            })}
            onChange={handleRestartClicked}
          >
            Create &amp; Add Another
          </Checkbox>
        </ActionContainer>

        {mode == TodoCreateMode.ROLLUP && showRollinGif && (
          <RollinImageContainer>
            <a
              href='https://www.youtube.com/watch?v=RYnFIRc0k6E'
              target='_blank'
              rel='noopener noreferrer'
            >
              <Image src='//i.imgur.com/6EEDSeh.gif' />
            </a>
          </RollinImageContainer>
        )}
      </Step>
    );
  }
);

export default Confirm;
